import {
  Button,
  ButtonGroup,
  Heading,
  Icon,
  List,
  Modal,
  Paragraph,
  Tag,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconTeaOutline } from '@zillow/constellation-icons';
import { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useApplicationsUpsellModal_EnableApplicationsMutation } from '../../../types/GraphqlTypes';
import {
  emitApplicationsUpsellEnableApplicationsClickEvent,
  emitApplicationsUpsellModalViewEvent,
} from '../../../constants/clickstreamEvents/applicationsUpsellModal';
import useModal from '../../../hooks/useModal';
import { logApolloError } from '../../../utils/errorUtils';
import { useAuth } from '@zg-rentals/auth-comet';

export const APPLICATIONS_UPSELL_MODAL_COOKIE = 'isApplicationsUpsellModalShown';

type ApplicationsUpsellModalProps = {
  listingAlias: string;
};

export default function ApplicationsUpsellModal({ listingAlias }: ApplicationsUpsellModalProps): JSX.Element | null {
  const { clearModal } = useModal();

  const [_, setCookie] = useCookies([APPLICATIONS_UPSELL_MODAL_COOKIE]);
  const { enqueueToast } = useToast();
  const { user } = useAuth();

  const [enableApplications] = useApplicationsUpsellModal_EnableApplicationsMutation({
    onError(err) {
      logApolloError(err, {
        details: 'ApplicationsUpsellModal - Error enabling applications for listing',
        listingAlias,
      });
      enqueueToast(<Toast tone="critical">There was an error enabling applications. Please try again</Toast>);
    },
    onCompleted() {
      enqueueToast(<Toast tone="success">Applications enabled for this listing.</Toast>);
    },
  });
  useEffect(() => {
    emitApplicationsUpsellModalViewEvent(user);

    return () => {
      setCookie(APPLICATIONS_UPSELL_MODAL_COOKIE, 'true', {
        path: '/',
      });
    };
  }, []);

  const handleClose = (): void => {
    clearModal();
  };

  const handleNotNowClick = (): void => {
    clearModal();
  };

  const handleReceiveApplicationsClick = (): void => {
    emitApplicationsUpsellEnableApplicationsClickEvent(user);

    enableApplications({
      variables: {
        listingAlias: listingAlias || '',
      },
    });

    clearModal();
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleClose()}
      size={{
        base: 'fullScreen',
        md: 'md',
      }}
      header={
        <Heading level={2} textStyle="heading-xs">
          Application sent
        </Heading>
      }
      body={
        <>
          <Icon size="xl" css={{ marginBlock: 'default' }}>
            <IconTeaOutline />
          </Icon>

          <Heading level={3} fontFamily="heading-alt" textStyle="heading-sm">
            Save time with Zillow applications
          </Heading>

          <Tag tone="info" css={{ marginBlock: 'default', textTransform: 'uppercase' }}>
            Free for landlords
          </Tag>

          <Paragraph>Let all applicants apply for this listing with Zillow and get access to:</Paragraph>
          <List.Root>
            <List.Item>Experian credit reports</List.Item>
            <List.Item>CIC eviction & criminal record checks</List.Item>
            <List.Item>Income & employment history</List.Item>
            <List.Item>Previous landlord contacts</List.Item>
          </List.Root>
        </>
      }
      footer={
        <ButtonGroup aria-label="modal actions">
          <TextButton onClick={handleNotNowClick}>Not now</TextButton>
          <Button emphasis="filled" onClick={handleReceiveApplicationsClick} tone="brand">
            Receive applications
          </Button>
        </ButtonGroup>
      }
    />
  );
}
